/**
 * The auth result of an OAuth authorization, i.e. what the backend found out about
 * the player behind the provider account.
 *
 * Kept free of any dependencies: both the OAuth flow and the login-hub screens
 * need it, so it must not pull anything else in.
 */
export const AUTH_RESULT = {
	PLAYER_FOUND: 'player_found',
	PLAYER_FOUND_DIRECT_PLAY: 'player_found_direct_play',
	PLAYER_NOT_FOUND: 'player_not_found',
	ERROR: 'error',
} as const;

export type AuthResult = typeof AUTH_RESULT[keyof typeof AUTH_RESULT];

const AUTH_RESULTS: AuthResult[] = Object.values(AUTH_RESULT);

/**
 * Narrows a raw `auth_result` value — it reaches us as an untyped string via the
 * mobile redirect's query parameter or the web popup's postMessage — to a known
 * result. Unknown values are `null`, i.e. treated as "no result".
 */
export const normalizeAuthResult = (value?: string | null): AuthResult | null =>
	AUTH_RESULTS.find((authResult) => authResult === value) ?? null;

/**
 * Auth result of the redirect the login-hub was (re)loaded with, `null` when the
 * player did not arrive here from an OAuth authorization.
 */
export const getAuthResultFromUrl = (): AuthResult | null =>
	normalizeAuthResult(new URLSearchParams(window.location.search).get('auth_result'));

export const isAuthenticatedResult = (authResult?: AuthResult | null): boolean =>
	authResult === AUTH_RESULT.PLAYER_FOUND
	|| authResult === AUTH_RESULT.PLAYER_FOUND_DIRECT_PLAY
	|| authResult === AUTH_RESULT.PLAYER_NOT_FOUND;
